import React, { useState, useEffect, useCallback } from 'react';
import { get } from 'lodash';
import {useTranslation} from 'react-i18next'
import usePermission from 'src/hooks/use-permission';
import { Redirect } from 'react-router-dom';
import Filters from 'src/pages/needs/filter';
import Divider from 'src/components/divider/index';
import PageTitle from 'src/components/page-title/index';
import FitModal from 'src/pages/needs/fits-modal';
import MatchModal from 'src/pages/needs/match-modal';
import RollbackModal from 'src/pages/needs/rollback-modal';
import CenterLoading from 'src/components/center-loading';
import ETLUpdate from "src/components/etl-update";
import NeedsTable from 'src/pages/needs/table';
import './style.scss';
import { notification } from 'antd';

/**
 * Renders a needs component.
 * @param {Object} props - The component props.
 * @param {Object} props.user - The logged user object.
 * @param {Object} props.filters - The filters object.
 * @param {Array} props.needs - The needs data.
 * @param {Function} props.filterNeeds - The function to filter the needs.
 * @param {boolean} props.listFetching - Indicates if the needs list is being fetched.
 * @param {Function} props.getDeallocations - The function to get deallocations.
 * @param {Function} props.onCancelFit - The function to cancel a fit.
 * @param {Function} props.onCreateFit - The function to create a fit.
 * @param {Function} props.onCreateMatch - The function to create a match.
 * @param {string} props.etlDate - The ETL date value.
 * @param {Array} props.fitDeallocations - The fit deallocations array.
 * @param {Function} props.onRollbackMatch - The function to rollback a match.
 * @param {Array} props.matchDeallocations - The match deallocations array.
 * @param {Object} props.positionMaps - The position maps object.
 * @returns {JSX.Element} The rendered Needs component.
 * @example
 *  <NeedsPage
        user={sessionUser}
        filters={filters}
        needs={needs}
        filterNeeds={filterNeeds}
        listFetching={listFetching}
        etlDate={etl}
        onCancelFit={handleCancelFit}
        onCreateFit={handleCreateFit}
        onCreateMatch={handleCreateMatch}
        onRollbackMatch={handleRollbackMatch}
        fitDeallocations={openDeallocations}
        matchDeallocations={matchDeallocations}
        getDeallocations={getDeallocations}
        positionMaps={positionMaps}
    />
 */
function Needs({
    user,
    filters,
    needs,
    filterNeeds,
    listFetching,
    getDeallocations,
    onCancelFit,
    onCreateFit,
    onCreateMatch,
    etlDate,
    fitDeallocations,
    onRollbackMatch,
    matchDeallocations,
    positionMaps
}) {
    const permissions = usePermission();
    const canCreate = get(permissions, 'needPermissions.canCreate');
    const [apiFilters, setApiFilters] = useState({});
    const [formFetching, setFormFetching] = useState(false);
    const [fitNeed, setFitNeed] = useState(null);
    const [matchNeed, setMatchNeed] = useState(null);
    const [confirmRollback, setConfirmRollback] = useState(null);
    const [filtersUpdated, setFiltersUpdated] = useState(false);
    const [pagination, setPagination] = useState({ limit: 50, offset: 0 });

    const { t } = useTranslation();

    /**
     * Get Needs and Deallocations' data upon changes.
     */
  const refreshData = useCallback(() => {
    filterNeeds(apiFilters)
    getDeallocations(apiFilters, pagination);
  }, [filterNeeds, getDeallocations, apiFilters, pagination]);

    useEffect(() => {
        /**
         * refreshData upon fit/match/rollback changes.
         */
        //NOTE: do not add refreshData() as a dependency, otherwise it will leak.
        if (fitNeed === null && matchNeed === null && confirmRollback === null) refreshData();
    }, [fitNeed, matchNeed, confirmRollback,pagination])

    /**
     * Cancels a fit with the specified fit ID.
     * 
     * @param {string} fitId - The ID of the fit to cancel.
     * @returns {Promise<void>} A promise that resolves when the fit is successfully canceled.
     */
    const cancelFit = async (fitId, cancelCategory, cancelSubcategory) => {
        setFormFetching(true);
        try {
            await onCancelFit(fitId, cancelCategory, cancelSubcategory);
            setFitNeed(null);
            setFormFetching(false);
            notification.success({message: t("success.fit.cancel")});
        } catch (e) {
            // notification.error({message: t("errors.fit.cancel")});
            setFormFetching(false);
            setFitNeed(null);
            return;
        }
    }

    /**
     * Creates a fit with the specified deallocation IDs and need ID.
     * 
     * @param {array} deallocationFitIds - The IDs of the deallocations.
     * @param {string} needId - The ID of the need.
     * @returns {Promise<void>} A promise that resolves when the fit is successfully created.
     */
    const createFit = async (deallocationFitIds, needId) => {
        setFormFetching(true);
        try {
            await onCreateFit(deallocationFitIds, needId);
            //NOTE - avoid rendering needs before creating all fits
            setTimeout(() => {
                setFitNeed(null);
            }, 500)
            setFormFetching(false);
            notification.success({message: t("success.fit.create")});
        } catch (e) {
            // notification.error({message: t("errors.fit.create")});
            setFormFetching(false);
            setFitNeed(null);
            return;
        }
    };

    /**
     * Creates a match with the specified fit ID.
     * @param {string} fitId - The ID of the fit.
     * @returns {Promise<void>} A promise that resolves when the match is successfully created.
     */
    const createMatch = async(fitId, startDate, allocationType) => {
      let message;

        setFormFetching(true);
        try {
            const response = (await onCreateMatch({id: fitId, match: 'Y', startDate, allocationType })).data;
            message = response.message
        } catch (e) {
          console.error(e);
        } finally {
          if (message.requisition === 'warn') {
            notification.error({message: t(`errors.fit.requisitionWarn`)})
          } else {
            message.match === 'ok' 
              ? notification.success({message: t(`success.fit.createMatch`)})
              : notification.error({message: t(`errors.fit.createMatch`)});

            message.closeRequisition === 'ok' 
              ? notification.success({message: t(`success.fit.closeRequisition`)})
              : notification.error({message: t(`errors.fit.closeRequisition`)});
          }

          setFormFetching(false);
          setMatchNeed(null);
        }
    }

    /**
     * Cancels a match with the specified deallocation ID, need ID, and optional description.
     * @param {string} deallocationId - The ID of the deallocation.
     * @param {string} needId - The ID of the need.
     * @param {string} [description] - The optional description for the rollback.
     * @returns {Promise<void>} A promise that resolves when the match is successfully canceled.
     */
    const rollbackMatch = async(deallocationId, needId, description, rollbackCategory, rollbackSubcategory) => {
        let matchRollback = false;
        let reopenedRequisition = false;

        setFormFetching(true);
        try {
            const {message} = (await onRollbackMatch({
                deallocationId,
                needId,
                match: 'N',
                active: 'N',
                rollbackDescription: description,
                rollbackCategory,
                rollbackSubcategory,
            })).data;

            matchRollback = message.matchRollback === 'ok'; 
            reopenedRequisition = message.reopenedRequisition === 'ok';
            setConfirmRollback(null);
            setFormFetching(false);
        } catch (e) {
            setFormFetching(false);
            setConfirmRollback(null);
            return;
        } finally {
          matchRollback 
            ? notification.success({message: t(`success.fit.rollbackMatch`)})
            : notification.error({message: t(`errors.fit.rollbackMatch`)});

          reopenedRequisition 
            ? notification.success({message: t(`success.fit.reopenRequisition`)})
            : notification.error({message: t(`errors.fit.reopenRequisition`)});
        }
    }

    /**
     * Change filters' values upon selection.
     */
    const handleFilter = useCallback(
        (value) => {
            setApiFilters(value);
            if (Object.values(value).some(value => value.length > 0)) setFiltersUpdated(true);
        },
        [setApiFilters]
    );

    useEffect(() => {
        /**
         * Send query params to the API to filter needs only if there are filters.
         */
        let hasFilters = Object.values(apiFilters).some(value => value.length > 0);

        if (hasFilters){
            filterNeeds(apiFilters);
        } else if (!hasFilters && filtersUpdated) {
            filterNeeds();
            setFiltersUpdated(false);
        }
    }, [apiFilters, filterNeeds, filtersUpdated]);

    const handlePaginationChange = (newPagination) => {
      setPagination(newPagination);
    };

return (
  <div className='opportunity'>
    {canCreate === false && <Redirect to='/responsibles' />}
    {canCreate === false && notification.error({ message: t("errors.access") })}
    <div className="opportunity-header__container">
      <PageTitle title={t('needsTitle')} />
      <ETLUpdate date={etlDate} />
    </div>
    <Divider />
    <div className='opportunity-filter'>
      <h2 className='opportunity-filter__container_counter'>
        {needs.length === 1 || needs.length === 0 ? `${needs.length} ${t('openNeed')}` : `${needs.length} ${t('openNeeds')}`}
      </h2>
      <Filters onApiFilter={handleFilter} filters={filters} />
      <div className='opportunity-table__container'>
        {listFetching ? (
          <CenterLoading />
        ) : (
          <NeedsTable
              user={user}
              data={needs}
              onClickFit={setFitNeed}
              onClickMatch={setMatchNeed}
              onClickCancel={setConfirmRollback}
              onPaginationChange={handlePaginationChange}
              pagination={{
                  current: Math.floor(pagination.offset / pagination.limit) + 1,
                  pageSize: pagination.limit
              }}
          />
        )}
      </div>
      {fitNeed ? (
        <FitModal
          loading={formFetching}
          need={fitNeed}
          user={user}
          deallocations={fitDeallocations}
          onSave={createFit}
          onClose={() => setFitNeed(null)}
          positionMaps={positionMaps}
          onCancelFit={cancelFit}
        />
      ) : null}

      {matchNeed && (
        <MatchModal
          loading={formFetching}
          need={matchNeed}
          deallocations={fitDeallocations}
          onSave={createMatch}
          onClose={() => setMatchNeed(null)}
        />
      )}

      {confirmRollback && (
        <RollbackModal
          onConfirm={rollbackMatch}
          onCancel={() => setConfirmRollback(null)}
          matchData={confirmRollback}
          deallocations={matchDeallocations}
          needs={needs}
          loading={formFetching}
        />
      )}
    </div>
  </div>
);

}

export default Needs;
